import { usePageTitle } from '../hooks/usePageTitle';
import React, { useState, useEffect } from 'react';
import { analyticsAPI } from '../lib/api';
import { getPhaseLabel } from '../lib/utils';
import {
  Users,
  TrendingUp,
  DollarSign,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Calendar,
  MessageSquare,
  BarChart3,
  Target,
  Loader2,
  Shield,
  FileText,
  ListChecks,
  ClipboardCheck,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';

const PHASE_ORDER = [
  'gap_analysis', 'application_implementation', 'desktop_nc_closure',
  'pre_assessment', 'final_nc_closure',
];

const KPICard = ({ icon: Icon, label, value, subValue, color, trend }) => (
  <Card className="p-4 border-0 shadow-md hover:shadow-lg transition-shadow" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-start justify-between">
      <div className={`p-2.5 rounded-xl ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
      {trend && (
        <Badge variant="outline" className={`text-xs ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
          {trend > 0 ? '+' : ''}{trend}%
        </Badge>
      )}
    </div>
    <p className="text-2xl font-bold mt-3">{value}</p>
    <p className="text-sm font-medium opacity-70">{label}</p>
    {subValue && <p className="text-xs text-muted-foreground mt-0.5">{subValue}</p>}
  </Card>
);

export const AnalyticsDashboardPage = () => {
  const [data, setData] = useState(null);
  usePageTitle('Analytics');
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const [overviewRes, readinessRes] = await Promise.all([
        analyticsAPI.getOverview(),
        analyticsAPI.getReadiness().catch(() => ({ data: { clients: [] } })),
      ]);
      setData(overviewRes.data);
      setReadiness(readinessRes.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) return null;

  const maxPhaseCount = Math.max(...PHASE_ORDER.map(p => data.phases[p]?.total || 0), 1);

  return (
    <div className="space-y-6 animate-in" data-testid="analytics-page">
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-[#0f4c81] via-[#1a6cb4] to-[#2196F3] p-6 text-white">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.2) 0%, transparent 50%)'}} />
        <div className="relative z-10">
          <h1 className="text-2xl font-heading font-bold">Analytics & Reports</h1>
          <p className="text-blue-100">Comprehensive business intelligence dashboard</p>
        </div>
      </div>

      {/* KPI Cards Row 1 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KPICard icon={Users} label="Total Clients" value={data.clients.total} subValue={`${data.clients.active} active, ${data.clients.approved} approved`} color="bg-blue-100 text-blue-700" />
        <KPICard icon={Target} label="Lead Conversion" value={`${data.leads.conversion_rate}%`} subValue={`${data.leads.by_status.won || 0} won / ${data.leads.total} total`} color="bg-green-100 text-green-700" />
        <KPICard icon={DollarSign} label="Total Revenue" value={`₹${(data.revenue.total || 0).toLocaleString()}`} subValue={`₹${(data.revenue.pending || 0).toLocaleString()} pending`} color="bg-emerald-100 text-emerald-700" />
        <KPICard icon={CheckCircle2} label="Task Completion" value={`${data.tasks.completion_rate}%`} subValue={`${data.tasks.completed}/${data.tasks.total} tasks done`} color="bg-purple-100 text-purple-700" />
      </div>

      {/* KPI Cards Row 2 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KPICard icon={Clock} label="Overdue Tasks" value={data.tasks.overdue} color="bg-red-100 text-red-700" />
        <KPICard icon={Calendar} label="Upcoming Visits" value={data.visits.upcoming} subValue={`${data.visits.completed} completed`} color="bg-amber-100 text-amber-700" />
        <KPICard icon={MessageSquare} label="Open Tickets" value={data.tickets.open} subValue={`${data.tickets.total} total`} color="bg-orange-100 text-orange-700" />
        <KPICard icon={BarChart3} label="Messages Sent" value={data.messages.total} color="bg-cyan-100 text-cyan-700" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Phase Pipeline */}
        <Card data-testid="phase-pipeline">
          <CardHeader>
            <CardTitle className="text-lg">NABH Phase Pipeline</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {PHASE_ORDER.map((phaseType) => {
              const p = data.phases[phaseType] || {};
              const total = p.total || 0;
              const inProgress = p.in_progress || 0;
              const completed = p.completed || 0;
              const blocked = p.blocked || 0;
              return (
                <div key={phaseType}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium">{getPhaseLabel(phaseType)}</p>
                    <span className="text-xs text-muted-foreground">{total} clients</span>
                  </div>
                  <div className="flex h-6 rounded-full overflow-hidden bg-slate-100">
                    {completed > 0 && (
                      <div
                        className="bg-green-500 flex items-center justify-center"
                        style={{ width: `${(completed / maxPhaseCount) * 100}%` }}
                        title={`${completed} completed`}
                      >
                        {completed > 0 && <span className="text-[10px] text-white font-medium">{completed}</span>}
                      </div>
                    )}
                    {inProgress > 0 && (
                      <div
                        className="bg-blue-500 flex items-center justify-center"
                        style={{ width: `${(inProgress / maxPhaseCount) * 100}%` }}
                        title={`${inProgress} in progress`}
                      >
                        {inProgress > 0 && <span className="text-[10px] text-white font-medium">{inProgress}</span>}
                      </div>
                    )}
                    {blocked > 0 && (
                      <div
                        className="bg-red-500 flex items-center justify-center"
                        style={{ width: `${(blocked / maxPhaseCount) * 100}%` }}
                        title={`${blocked} blocked`}
                      >
                        <span className="text-[10px] text-white font-medium">{blocked}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            <div className="flex gap-4 pt-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-green-500" /> Completed</span>
              <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-blue-500" /> In Progress</span>
              <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-red-500" /> Blocked</span>
            </div>
          </CardContent>
        </Card>

        {/* Lead Funnel */}
        <Card data-testid="lead-funnel">
          <CardHeader>
            <CardTitle className="text-lg">Lead Funnel</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {['new', 'contacted', 'qualified', 'proposal_sent', 'won', 'lost'].map((status) => {
              const count = data.leads.by_status[status] || 0;
              const pct = data.leads.total > 0 ? (count / data.leads.total) * 100 : 0;
              const colors = {
                new: 'bg-slate-400', contacted: 'bg-blue-400', qualified: 'bg-cyan-500',
                proposal_sent: 'bg-amber-500', won: 'bg-green-500', lost: 'bg-red-400'
              };
              return (
                <div key={status}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm capitalize">{status.replace('_', ' ')}</p>
                    <span className="text-sm font-medium">{count}</span>
                  </div>
                  <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
                    <div className={`h-full rounded-full ${colors[status]}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Revenue & Checklist Progress */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="revenue-breakdown">
          <CardHeader>
            <CardTitle className="text-lg">Revenue Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Collected</p>
                  <p className="text-xl font-bold text-green-700">₹{(data.revenue.total || 0).toLocaleString()}</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-green-300" />
              </div>
              <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Pending</p>
                  <p className="text-xl font-bold text-amber-700">₹{(data.revenue.pending || 0).toLocaleString()}</p>
                </div>
                <Clock className="w-8 h-8 text-amber-300" />
              </div>
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Overdue</p>
                  <p className="text-xl font-bold text-red-700">₹{(data.revenue.overdue || 0).toLocaleString()}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-300" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="checklist-progress">
          <CardHeader>
            <CardTitle className="text-lg">Checklist Progress by Phase</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {PHASE_ORDER.map((pt) => {
              const cp = data.checklist_progress[pt] || { completed: 0, total: 0 };
              const pct = cp.total > 0 ? Math.round((cp.completed / cp.total) * 100) : 0;
              return (
                <div key={pt}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm">{getPhaseLabel(pt)}</p>
                    <span className="text-xs text-muted-foreground">{cp.completed}/{cp.total}</span>
                  </div>
                  <Progress value={pct} className="h-2" />
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Accreditation Readiness Scores */}
      {readiness?.clients?.length > 0 && (
        <Card data-testid="readiness-scores-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" /> Accreditation Readiness Scores
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {readiness.clients.map((client) => {
              const b = client.breakdown;
              const scoreColor = client.overall_score >= 75 ? 'text-green-600' :
                client.overall_score >= 50 ? 'text-amber-600' : 'text-red-600';
              const scoreBg = client.overall_score >= 75 ? 'bg-green-50 border-green-200' :
                client.overall_score >= 50 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200';
              return (
                <div key={client.client_id} className={`p-4 rounded-lg border ${scoreBg}`} data-testid={`readiness-${client.client_id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-semibold text-sm">{client.client_name}</p>
                      <p className="text-xs text-muted-foreground">{client.accreditation_standard || 'NABH'} &middot; {getPhaseLabel(client.current_phase)}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${scoreColor}`}>{client.overall_score}%</p>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Readiness</p>
                    </div>
                  </div>
                  <Progress value={client.overall_score} className="h-2 mb-3" />
                  <div className="grid grid-cols-5 gap-2">
                    {[
                      { key: 'phases', label: 'Phases', icon: Target, detail: `${b.phases.completed}/${b.phases.total}` },
                      { key: 'checklists', label: 'Checklists', icon: ListChecks, detail: `${b.checklists.completed}/${b.checklists.total}` },
                      { key: 'tasks', label: 'Tasks', icon: ClipboardCheck, detail: `${b.tasks.verified}/${b.tasks.total}` },
                      { key: 'documents', label: 'Docs', icon: FileText, detail: `${b.documents.count}` },
                      { key: 'ncs', label: 'NCs', icon: AlertTriangle, detail: `${b.ncs.closed}/${b.ncs.total}` },
                    ].map(item => {
                      const Icon = item.icon;
                      return (
                        <div key={item.key} className="text-center p-1.5 rounded bg-white/60">
                          <Icon className="w-3.5 h-3.5 mx-auto mb-0.5 text-muted-foreground" />
                          <p className="text-xs font-medium">{b[item.key].score}%</p>
                          <p className="text-[10px] text-muted-foreground">{item.label}</p>
                          <p className="text-[10px] text-muted-foreground">{item.detail}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AnalyticsDashboardPage;
